import { FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { ClaimRewardsError, useClaimRewards } from "@/hooks/user/useClaimRewards";

type UseClaimTTEHTERewardsProps = {
  onClaimSuccess?: () => void;
  onClaimError?: (error: ClaimRewardsError) => void;
};

/**
 * Hook for claiming Trade-to-Earn and Hold-to-Earn rewards only.
 * Filters out SPINNING_WHEEL rewards from the claimable/claimable-soon endpoints.
 * Used by the main "Claim Rewards" button to show only TTE+HTE rewards.
 */
export const useClaimTTEHTERewards = ({ onClaimSuccess, onClaimError }: UseClaimTTEHTERewardsProps) => {
  const apiHelper = useApi();
  const isClaimsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.CLAIMS_HUB_ENABLE);

  return useClaimRewards({
    onClaimSuccess,
    onClaimError,
    queryKeys: {
      claimableRewards: QueryKeys.HUB_REWARDS_CLAIMABLE,
      claimableSoonRewards: QueryKeys.HUB_REWARDS_CLAIMABLE_SOON,
    },
    fetchers: {
      getClaimableRewards: async (userAddress) => {
        // Backend now filters by campaignTypes - no client-side filtering needed
        const claimableRewards = await apiHelper.getOrThrow(API_ENDPOINT.HUB_REWARDS_CLAIMABLE, {
          queryParams: { defiAddress: userAddress, campaignTypes: "TTE,HTE" },
        });

        return claimableRewards.claimableEpochs;
      },
      simulateClaim: async (claimItems, chainId) => {
        await apiHelper.postOrThrow(API_ENDPOINT.HUB_REWARDS_SIMULATE, {
          bodyParams: { claimItems, chainId },
        });
      },
      getClaimableSoonRewards: async (userAddress) => {
        // Backend now filters by campaignTypes - no client-side filtering needed
        const claimableSoonRewards = await apiHelper.getOrThrow(API_ENDPOINT.HUB_REWARDS_CLAIMABLE_SOON, {
          queryParams: { defiAddress: userAddress, campaignTypes: "TTE,HTE" },
        });

        return claimableSoonRewards.claimableSoonEpochs;
      },
    },
    metricsEvent: METRICS_EVENTS.CLAIM_REWARDS,
    enabled: isClaimsEnabled,
  });
};
